import React, { useEffect, useState, useRef, Children } from 'react';
import { ChevronLeft, Send, Calendar, Clock, Bell, CheckCircle2, PackageCheck, AlertCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import { ChatBubble } from '../components/ChatBubble';
import { Badge } from '../components/Badge';
import { Button } from '../components/Button';
import { BookingRequestCard } from '../components/BookingRequestCard';
import { BookingRequest } from '../App';
interface ChatProps {
  bookingId: string;
  currentBooking: BookingRequest | null;
  onBack: () => void;
  onRequestBooking?: () => void;
  onEditRequest?: () => void;
  onStartRental?: () => void;
  onEndRental?: () => void;
  onReviewInspect?: () => void;
  bookingStarted?: boolean;
  bookingCompleted?: boolean;
}
export function Chat({
  onBack,
  currentBooking,
  onRequestBooking,
  onEditRequest,
  onStartRental,
  onEndRental,
  onReviewInspect,
  bookingStarted = false,
  bookingCompleted = false
}: ChatProps) {
  const [message, setMessage] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  // Determine booking status for display
  const bookingStatus = currentBooking?.status || null;
  const hasBooking = currentBooking !== null;
  const isPending = bookingStatus === 'pending';
  const isConfirmed = bookingStatus === 'approved' || bookingStatus === 'active';
  const isCompleted = bookingStatus === 'completed';
  const isInProgress = bookingStarted && !bookingCompleted;
  const isItemReturned = bookingCompleted;
  const [messages, setMessages] = useState([{
    id: '1',
    message: "Ask questions before requesting to book. For safety, contact details can't be shared before a booking is confirmed.",
    isOwn: false,
    timestamp: '',
    isSystem: true
  }, {
    id: '2',
    message: 'Hi! Is the drill set still available for Dec 15-18?',
    isOwn: true,
    timestamp: '2:30 PM'
  }, {
    id: '3',
    message: "Yes, it's available! I can meet at the Starbucks on Market St for pickup.",
    isOwn: false,
    timestamp: '2:35 PM',
    avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=200&q=80'
  }, {
    id: '4',
    message: 'Perfect! Does it come with all the drill bits shown in the photos?',
    isOwn: true,
    timestamp: '2:37 PM'
  }, {
    id: '5',
    message: 'Yes, everything shown is included plus the carrying case and charger.',
    isOwn: false,
    timestamp: '2:40 PM',
    avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=200&q=80'
  }]);
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  }, [messages]);
  const handleSend = () => {
    if (message.trim()) {
      const newMessage = {
        id: Date.now().toString(),
        message: message.trim(),
        isOwn: true,
        timestamp: new Date().toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit'
        })
      };
      setMessages([...messages, newMessage]);
      setMessage('');
    }
  };
  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };
  const formatDateTime = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      month: 'numeric',
      day: 'numeric',
      year: 'numeric'
    }) + ' at ' + date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit'
    });
  };
  const formatTime = (time: string) => {
    return time.replace(' ', '').toLowerCase();
  };
  // Mock data for confirmed booking display
  const itemTitle = currentBooking?.itemTitle || 'Wireless Power Drill';
  const dateRange = currentBooking ? `${formatDate(currentBooking.startDate)} - ${formatDate(currentBooking.endDate)}` : 'Dec 15 - Dec 18';
  const pickupTime = currentBooking ? formatTime(currentBooking.pickupTime) : '9:00am';
  const dropoffTime = currentBooking ? formatTime(currentBooking.dropoffTime) : '5:00pm';
  const container = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05
      }
    }
  };
  const item = {
    hidden: {
      opacity: 0,
      y: 10
    },
    show: {
      opacity: 1,
      y: 0
    }
  };
  // Badge variant based on booking lifecycle:
  // no booking → no badge | pending → Requested | approved → Confirmed | started → In Progress | completed → Completed
  const getBadgeProps = () => {
    if (isItemReturned) {
      return {
        variant: 'secondary' as const,
        text: 'Item Returned'
      };
    }
    if (isInProgress) {
      return {
        variant: 'primary' as const,
        text: 'Booking in Progress'
      };
    }
    if (isCompleted) {
      return {
        variant: 'neutral' as const,
        text: 'Completed'
      };
    }
    if (isConfirmed) {
      return {
        variant: 'success' as const,
        text: 'Confirmed'
      };
    }
    if (isPending) {
      return {
        variant: 'warning' as const,
        text: 'Requested'
      };
    }
    return null;
  };
  const badgeProps = getBadgeProps();
  return <div className="min-h-screen bg-cream flex flex-col" data-id="element-736">
      {/* Fixed Header */}
      <div className="sticky top-0 z-50 bg-white border-b border-sand" data-id="element-737">
        <div className="max-w-md mx-auto px-3 py-2" data-id="element-738">
          <div className="flex items-start gap-2" data-id="element-739">
            <button onClick={onBack} className="p-1.5 -ml-1 mt-0.5 hover:bg-sand/20 rounded-full transition-colors flex-shrink-0" data-id="element-740">
              <ChevronLeft size={22} className="text-charcoal" data-id="element-741" />
            </button>

            <div className="flex-1 min-w-0" data-id="element-742">
              <h1 className="text-sm font-bold text-charcoal" data-id="element-743">Oliver M.</h1>
              <p className="text-xs text-moss-gray mt-0.5 truncate" data-id="element-744">
                {itemTitle} · {dateRange}
              </p>
            </div>

            <div className="flex flex-col items-end gap-1.5 flex-shrink-0" data-id="element-745">
              {badgeProps && <Badge variant={badgeProps.variant} size="sm" data-id="element-746">
                  {badgeProps.text}
                </Badge>}

              {/* Start Booking Button: only when confirmed and NOT yet started */}
              {isConfirmed && !isInProgress && !isItemReturned && onStartRental && <button onClick={onStartRental} className="px-3 py-1.5 bg-dark-green text-white text-xs font-bold rounded hover:bg-dark-green/90 active:scale-95 transition-all whitespace-nowrap" data-id="element-747">
                    Start Booking
                  </button>}

              {/* Complete Booking Button: shown when booking is in progress */}
              {isInProgress && onEndRental && <button onClick={onEndRental} className="px-3 py-1.5 bg-dark-green text-white text-[11px] font-bold rounded hover:bg-dark-green/90 active:scale-95 transition-all whitespace-nowrap min-w-[110px] h-8 flex items-center justify-center" data-id="element-748">
                  Complete Booking
                </button>}

              {/* Review & Inspect Button: shown when item is returned */}
              {isItemReturned && onReviewInspect && <button onClick={onReviewInspect} className="px-3 py-1.5 bg-dark-green text-white text-[11px] font-bold rounded hover:bg-dark-green/90 active:scale-95 transition-all whitespace-nowrap min-w-[110px] h-8 flex items-center justify-center" data-id="element-749">
                  Review & Inspect
                </button>}

              {/* Edit Request Button: only when pending */}
              {isPending && !isInProgress && !isItemReturned && onEditRequest && <button onClick={onEditRequest} className="px-3 py-1.5 bg-dark-green/10 text-dark-green text-xs font-bold rounded hover:bg-dark-green/20 active:scale-95 transition-all whitespace-nowrap" data-id="element-750">
                    Edit Request
                  </button>}
            </div>
          </div>

          {/* Request Booking Button: only when NO booking exists at all */}
          {!hasBooking && !isInProgress && onRequestBooking && <div className="mt-2" data-id="element-751">
              <Button variant="primary" fullWidth onClick={onRequestBooking} className="h-10 font-semibold text-sm" data-id="element-752">
                Request Booking
              </Button>
            </div>}
        </div>
      </div>

      {/* Messages Thread */}
      <div className="flex-1 overflow-y-auto pb-24" data-id="element-753">
        <motion.div variants={container} initial="hidden" animate="show" className="max-w-md mx-auto px-3 py-3 space-y-2" data-id="element-754">
          {messages.map(msg => <motion.div key={msg.id} variants={item} data-id="element-755">
              <ChatBubble message={msg.message} isOwn={msg.isOwn} timestamp={msg.timestamp} isSystem={msg.isSystem} avatar={msg.avatar} data-id="element-756" />
            </motion.div>)}

          {/* Booking Request Card (System Message) */}
          {currentBooking && <motion.div variants={item} data-id="element-757">
              <SystemCard variant="request" data-id="element-758">
                <p className="text-[11px] font-bold text-charcoal mb-1.5" data-id="element-759">
                  Booking Request submitted for {itemTitle} on{' '}
                  {formatDateTime(currentBooking.submittedAt)}
                </p>
                <div className="space-y-1 text-[10px] text-charcoal/80" data-id="element-760">
                  <div className="flex items-center gap-1.5" data-id="element-761">
                    <Calendar size={10} className="text-dark-green" data-id="element-762" />
                    <span data-id="element-763">{dateRange}</span>
                  </div>
                  <div className="flex items-center gap-1.5" data-id="element-764">
                    <Clock size={10} className="text-dark-green" data-id="element-765" />
                    <span data-id="element-766">Pick-up: {pickupTime}</span>
                  </div>
                  <div className="flex items-center gap-1.5" data-id="element-767">
                    <Clock size={10} className="text-dark-green" data-id="element-768" />
                    <span data-id="element-769">Drop-off: {dropoffTime}</span>
                  </div>
                </div>
                <p className="text-[10px] text-moss-gray mt-1.5 italic" data-id="element-770">
                  Request expires on {formatDateTime(currentBooking.expiresAt)}
                </p>
              </SystemCard>
            </motion.div>}

          {/* Booking Confirmation Card (shown when confirmed) */}
          {isConfirmed && <motion.div variants={item} data-id="element-771">
              <SystemCard variant="confirmed" data-id="element-772">
                <p className="text-[11px] font-bold text-charcoal mb-1.5" data-id="element-773">
                  Booking Request Confirmed for {itemTitle}!
                </p>
                <div className="space-y-1 text-[10px] text-charcoal/80" data-id="element-774">
                  <div className="flex items-center gap-1.5" data-id="element-775">
                    <Calendar size={10} className="text-dark-green" data-id="element-776" />
                    <span data-id="element-777">{dateRange}</span>
                  </div>
                  <div className="flex items-center gap-1.5" data-id="element-778">
                    <Clock size={10} className="text-dark-green" data-id="element-779" />
                    <span data-id="element-780">Pick-up: {pickupTime}</span>
                  </div>
                  <div className="flex items-center gap-1.5" data-id="element-781">
                    <Clock size={10} className="text-dark-green" data-id="element-782" />
                    <span data-id="element-783">Drop-off: {dropoffTime}</span>
                  </div>
                </div>
              </SystemCard>
            </motion.div>}

          {/* Booking Starts Today Notification (shown when active/confirmed) */}
          {isConfirmed && <motion.div variants={item} data-id="element-784">
              <SystemCard variant="notification" data-id="element-785">
                <div className="flex items-center gap-1.5 mb-1.5" data-id="element-786">
                  <Bell size={12} className="text-dark-green" data-id="element-787" />
                  <p className="text-[11px] font-bold text-charcoal" data-id="element-788">
                    Your booking starts today!
                  </p>
                </div>
                <div className="space-y-1 text-[10px] text-charcoal/80" data-id="element-789">
                  <div className="flex items-center gap-1.5" data-id="element-790">
                    <Calendar size={10} className="text-dark-green" data-id="element-791" />
                    <span data-id="element-792">{dateRange}</span>
                  </div>
                  <div className="flex items-center gap-1.5" data-id="element-793">
                    <Clock size={10} className="text-dark-green" data-id="element-794" />
                    <span data-id="element-795">Pick-up: {pickupTime}</span>
                  </div>
                </div>
              </SystemCard>
            </motion.div>}

          {/* Post-confirmation messages */}
          {isConfirmed && <>
              <motion.div variants={item} data-id="element-796">
                <ChatBubble message="Great, your booking is confirmed! I'll have everything ready for pickup. See you at the Starbucks on Market St." isOwn={false} timestamp="3:15 PM" avatar="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=200&q=80" data-id="element-797" />
              </motion.div>
              <motion.div variants={item} data-id="element-798">
                <ChatBubble message="Awesome, thanks! See you then 👍" isOwn={true} timestamp="3:18 PM" data-id="element-799" />
              </motion.div>
            </>}

          {/* Booking Started System Message (shown after start booking form submitted) */}
          {isInProgress && <motion.div variants={item} data-id="element-800">
              <SystemCard variant="started" data-id="element-801">
                <div className="flex items-center gap-1.5" data-id="element-802">
                  <CheckCircle2 size={12} className="text-dark-green" data-id="element-803" />
                  <p className="text-[11px] font-bold text-charcoal" data-id="element-804">
                    You Started Booking!
                  </p>
                </div>
              </SystemCard>
            </motion.div>}

          {/* Booking Ends Today Notification (shown when in progress - dropoff day) */}
          {isInProgress && <motion.div variants={item} data-id="element-805">
              <SystemCard variant="ending" data-id="element-806">
                <div className="flex items-center gap-1.5 mb-1.5" data-id="element-807">
                  <Bell size={12} className="text-clay" data-id="element-808" />
                  <p className="text-[11px] font-bold text-charcoal" data-id="element-809">
                    Your booking ends today!
                  </p>
                </div>
                <div className="space-y-1 text-[10px] text-charcoal/80" data-id="element-810">
                  <div className="flex items-center gap-1.5" data-id="element-811">
                    <Calendar size={10} className="text-clay" data-id="element-812" />
                    <span data-id="element-813">{dateRange}</span>
                  </div>
                  <div className="flex items-center gap-1.5" data-id="element-814">
                    <Clock size={10} className="text-clay" data-id="element-815" />
                    <span data-id="element-816">Drop-off: {dropoffTime}</span>
                  </div>
                </div>
                <p className="text-[10px] font-bold text-clay mt-1.5" data-id="element-817">
                  Please Complete Booking
                </p>
              </SystemCard>
            </motion.div>}

          {/* Item Returned Notification (shown after borrower completes booking) */}
          {isItemReturned && <>
              <motion.div variants={item} data-id="element-818">
                <SystemCard variant="returned" data-id="element-819">
                  <div className="flex items-center gap-1.5 mb-1.5" data-id="element-820">
                    <PackageCheck size={12} className="text-dark-green" data-id="element-821" />
                    <p className="text-[11px] font-bold text-charcoal" data-id="element-822">
                      You has marked the item as returned and uploaded return
                      photos.
                    </p>
                  </div>
                  <p className="text-[10px] text-charcoal/70 leading-relaxed" data-id="element-823">
                    Please leave a review for Oliver M. and inspect your item's
                    condition within 7 days before the booking is automatically
                    closed and complete.
                  </p>
                </SystemCard>
              </motion.div>

              {/* Follow-up Reminder Card */}
              <motion.div variants={item} data-id="element-824">
                <SystemCard variant="reminder" data-id="element-825">
                  <div className="flex items-center gap-1.5 mb-1.5" data-id="element-826">
                    <AlertCircle size={12} className="text-clay" data-id="element-827" />
                    <p className="text-[11px] font-bold text-charcoal" data-id="element-828">
                      Reminder: Review & inspect your item
                    </p>
                  </div>
                  <p className="text-[10px] text-charcoal/70 leading-relaxed" data-id="element-829">
                    You have 6 days remaining to review and inspect your item's
                    condition before the booking is automatically closed and
                    complete.
                  </p>
                </SystemCard>
              </motion.div>
            </>}

          <div ref={messagesEndRef} data-id="element-830" />
        </motion.div>
      </div>

      {/* Fixed Message Input Area */}
      <div className="fixed bottom-20 left-0 right-0 bg-white border-t border-sand z-50" data-id="element-831">
        <div className="max-w-md mx-auto px-3 py-2 flex gap-2 items-center" data-id="element-832">
          <input type="text" value={message} onChange={e => setMessage(e.target.value)} onKeyPress={e => e.key === 'Enter' && handleSend()} placeholder="Type message here..." className="flex-1 h-[34px] px-2 border border-sand rounded text-sm text-charcoal placeholder-moss-gray focus:outline-none focus:ring-1 focus:ring-dark-green focus:border-dark-green" data-id="element-833" />
          <button onClick={handleSend} disabled={!message.trim()} className="w-[34px] h-[34px] bg-dark-green text-white rounded flex items-center justify-center active:scale-95 transition-transform disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0" data-id="element-834">
            <Send size={16} data-id="element-835" />
          </button>
        </div>
      </div>
    </div>;
}
// System Card component for booking-related messages in the thread
function SystemCard({
  children,
  variant
}: {
  children: React.ReactNode;
  variant: 'request' | 'confirmed' | 'notification' | 'started' | 'ending' | 'returned' | 'reminder';
}) {
  const bgStyles = {
    request: 'bg-sand/30 border-sand',
    confirmed: 'bg-light-green/20 border-dark-green/20',
    notification: 'bg-sky-blue/15 border-sky-blue/30',
    started: 'bg-dark-green/10 border-dark-green/20',
    ending: 'bg-clay/10 border-clay/20',
    returned: 'bg-light-green/15 border-dark-green/20',
    reminder: 'bg-clay/8 border-clay/15'
  };
  return <motion.div initial={{
    opacity: 0,
    y: 8
  }} animate={{
    opacity: 1,
    y: 0
  }} className={`mx-2 rounded-md border p-2.5 ${bgStyles[variant]}`} data-id="element-836">
      {children}
    </motion.div>;
}